import { isAxiosError } from 'axios'
import type { BffAuthErrorEnvelope } from './types'

export const LOGIN_STATE_EXPIRED_CODE = 'ERROR-0419'
export const INVALID_MFA_CODE_CODE = 'ERROR-0420'
export const FORGOT_PASSWORD_ERROR_CODE = 'ERROR-0400'
export const TOO_MANY_ATTEMPTS_MESSAGE = 'Too many attempts. Please try again shortly.'

const UI_MESSAGES: Record<string, string> = {
    [LOGIN_STATE_EXPIRED_CODE]: 'Your login session has expired. Please start again.',
    [INVALID_MFA_CODE_CODE]: 'Incorrect code entered. Please check the code and try again.',
}

export class LoginStateExpiredError extends Error {}

export function getBffAuthErrorMessage(error: unknown): string {
    if (isAxiosError<BffAuthErrorEnvelope>(error) && error.response?.data?.message) {
        return error.response.data.message
    }
    return error instanceof Error ? error.message : String(error)
}

function getBffAuthErrorCode(error: unknown): string | undefined {
    return isAxiosError<BffAuthErrorEnvelope>(error) ? error.response?.data?.code : undefined
}

export function isLoginStateExpiredError(error: unknown): boolean {
    return getBffAuthErrorCode(error) === LOGIN_STATE_EXPIRED_CODE
}

export function isInvalidMfaCodeError(error: unknown): boolean {
    return getBffAuthErrorCode(error) === INVALID_MFA_CODE_CODE
}

export function isForgotPasswordError(error: unknown): boolean {
    return getBffAuthErrorCode(error) === FORGOT_PASSWORD_ERROR_CODE
}

// A 429 from this endpoint is API Gateway's default throttle response with no JSON body at
// all, so `error.response?.data?.code` is always undefined — check the HTTP status instead.
export function isTooManyAttemptsError(error: unknown): boolean {
    return isAxiosError(error) && error.response?.status === 429
}

export function getBffAuthUiMessage(error: unknown): string {
    const code = getBffAuthErrorCode(error)
    if (code && code in UI_MESSAGES) {
        return UI_MESSAGES[code]
    }
    return getBffAuthErrorMessage(error)
}

export function rethrowIfLoginStateExpired(error: unknown): void {
    if (isLoginStateExpiredError(error)) {
        throw new LoginStateExpiredError(getBffAuthUiMessage(error))
    }
}
